import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelS } from '@sorare/core/src/atoms/typography';
import { DrukWide24 } from '@sorare/core/src/components/marketing/typography';
import { isType } from '@sorare/core/src/gql';
import { glossary } from '@sorare/core/src/lib/glossary';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GenericSkinItem, Task } from './Task';
import { Category_ManagerProgressionCategory } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  gap: var(--quadruple-unit);
  @media ${desktopAndAbove} {
    flex-direction: row;
    gap: calc(9 * var(--unit));
  }
`;
const AsideVertical = styled(Vertical)`
  max-width: 360px;
  align-items: center;
  margin: auto;
  @media ${desktopAndAbove} {
    margin: initial;
    margin-top: calc(10 * var(--unit));
    align-items: initial;
  }
`;
const Badge = styled.img`
  width: calc(20 * var(--unit));
  aspect-ratio: 1/1;
`;
const StyledLabelS = styled(LabelS)`
  text-transform: uppercase;
`;
const Tasks = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: max-content;
  gap: var(--intermediate-unit);
  @media ${desktopAndAbove} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

type Props = {
  category: Category_ManagerProgressionCategory;
  onClaim: ({
    pointsEarned,
    rewards,
  }: {
    rewards: GenericSkinItem[];
    pointsEarned: number;
  }) => void;
  isMe: boolean;
};

type Category_ManagerProgressionTask =
  Category_ManagerProgressionCategory['categoryTasks'][number] & {
    __typename: 'ManagerProgressionTask';
  };

export const Category = ({ category, onClaim, isMe }: Props) => {
  const tasks = category.categoryTasks.filter(task =>
    isType(task, 'ManagerProgressionTask')
  ) as Category_ManagerProgressionTask[];

  return (
    <Root>
      <AsideVertical gap={5}>
        <Badge src={category.badgeUrl} alt={category.name} />
        <Vertical gap={1}>
          <DrukWide24>{category.title}</DrukWide24>
          <BodyM as="p">{category.description}</BodyM>
        </Vertical>
        <Vertical gap={0.5}>
          <StyledLabelS as="p" bold>
            <FormattedMessage {...glossary.claimed} />
          </StyledLabelS>
          <DrukWide24>
            {
              tasks.filter(
                task => task.aasmState === ManagerProgressionTaskState.CLAIMED
              ).length
            }
            {' / '}
            {tasks.length}
          </DrukWide24>
        </Vertical>
      </AsideVertical>
      <Tasks>
        {tasks.map(task => (
          <Task
            key={task.id}
            category={category.name}
            task={task}
            onClaim={onClaim}
            isMe={isMe}
          />
        ))}
      </Tasks>
    </Root>
  );
};

Category.fragments = {
  managerProgressionCategory: gql`
    fragment Category_ManagerProgressionCategory on ManagerProgressionCategory {
      id
      name
      title
      description
      badgeUrl
      categoryTasks {
        ... on ManagerTaskInterface {
          id
          aasmState
        }
        ... on ManagerProgressionTask {
          id
          ...Task_ManagerProgressionTask
        }
      }
    }
    ${Task.fragments.managerProgressionTask}
  ` as TypedDocumentNode<Category_ManagerProgressionCategory>,
};
